import { useQuery } from "@tanstack/react-query";
import type { Announcement } from "@workin/shared";
import { useEffect, useState } from "react";
import { api } from "../lib/api";

function isDismissed(id: string): boolean {
  return localStorage.getItem(`announcement_dismissed_${id}`) === "1";
}

function dismiss(id: string) {
  localStorage.setItem(`announcement_dismissed_${id}`, "1");
}

export function AnnouncementBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [, setTick] = useState(0);

  const { data: announcement } = useQuery({
    queryKey: ["announcement-active"],
    queryFn: () => api.get<Announcement | null>("/api/announcements/active"),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (!announcement?.expiresAt) return;
    const ms = new Date(announcement.expiresAt).getTime() - Date.now();
    if (ms <= 0) return;
    const id = setTimeout(() => setTick((n) => n + 1), ms);
    return () => clearTimeout(id);
  }, [announcement]);

  if (!announcement) return null;
  if (!announcement.message?.trim()) return null;
  if (dismissed || isDismissed(announcement.id)) return null;
  if (announcement.expiresAt && new Date(announcement.expiresAt).getTime() <= Date.now()) return null;

  const handleClose = () => {
    dismiss(announcement.id);
    setDismissed(true);
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-start gap-3">
      <svg
        className="w-5 h-5 text-amber-500 shrink-0 mt-0.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        viewBox="0 0 24 24"
      >
        <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
      <p className="text-sm text-amber-900 flex-1">{announcement.message}</p>
      <button
        type="button"
        onClick={handleClose}
        aria-label="Chiudi comunicazione"
        className="text-amber-400 hover:text-amber-700 transition-colors shrink-0"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
